import { useMemo, useRef } from 'react';
import {
  Home,
  ShoppingCart,
  ChefHat,
  Boxes,
  Tag,
  Users,
  BarChart3,
  Settings,
  History,
  Command,
  Wrench,
  LayoutGrid,
  BookOpen,
  Receipt,
  MonitorPlay,
  Sun,
} from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { usePermissions } from '@/hooks/usePermissions';
import { useModuleSettings, type ModuleKey } from '@/hooks/useModuleSettings';
import { useIndustryTerms } from '@/hooks/useIndustryTerms';

// A single sub-nav entry inside a rail group. Mirrors the visibility contract
// used by the Blaze Sidebar (permissions + org modules + role exclusions) so
// the two layouts stay in sync about what a user is allowed to see.
export interface LsNavItem {
  label: string;
  to: string;
  icon: React.ElementType;
  permission?: string | string[];
  requireAll?: boolean;
  superadminOnly?: boolean;
  excludeRoles?: string[];
  moduleKey?: ModuleKey | ModuleKey[];
  end?: boolean;
}

// A top-level section represented by one icon on the left rail. Selecting it
// reveals its `items` in the contextual sub-nav column.
export interface LsNavGroup {
  id: string;
  label: string;
  icon: React.ElementType;
  /** Default landing route when the rail icon is clicked. */
  to: string;
  /** Route prefixes that mark this group as active. */
  matchPrefixes: string[];
  items: LsNavItem[];
}

// Base structure. Labels for kitchen/floor-plan are overridden per-industry in
// the hook below, matching the Blaze sidebar behaviour.
const NAV_GROUPS: LsNavGroup[] = [
  {
    id: 'home',
    label: 'Home',
    icon: Home,
    to: '/dashboard',
    matchPrefixes: ['/dashboard', '/morning-brief'],
    items: [
      { label: 'Dashboard', to: '/dashboard', icon: Home, end: true },
      { label: 'Morning Brief', to: '/morning-brief', icon: Sun, permission: 'reports.view_sales', moduleKey: 'reports' },
    ],
  },
  {
    id: 'sell',
    label: 'Sell',
    icon: ShoppingCart,
    to: '/sell',
    matchPrefixes: ['/sell', '/floor-plan', '/kds', '/kitchen-stations', '/settle-register', '/close-register'],
    items: [
      { label: 'Sell', to: '/sell', icon: ShoppingCart, permission: 'pos.access', moduleKey: 'pos', end: true },
      { label: 'Floor Plan', to: '/floor-plan', icon: LayoutGrid, permission: 'pos.access', moduleKey: 'floorPlan' },
      { label: 'Order History', to: '/sell/sales-history', icon: Receipt, permission: 'orders.view' },
      { label: 'Kitchen (KDS)', to: '/kds', icon: ChefHat, permission: 'orders.view', excludeRoles: ['cashier', 'employee'], moduleKey: 'kds' },
      { label: 'KDS Stations', to: '/kitchen-stations', icon: MonitorPlay, permission: 'settings.view', excludeRoles: ['cashier', 'employee'], moduleKey: 'kdsStations' },
    ],
  },
  {
    id: 'catalog',
    label: 'Catalog',
    icon: Tag,
    to: '/stock-control',
    matchPrefixes: ['/stock-control', '/standardization', '/products'],
    items: [
      { label: 'Inventory', to: '/stock-control', icon: Boxes, permission: ['inventory.view', 'menu.view'], moduleKey: ['inventory', 'products'] },
      { label: 'Standardization', to: '/standardization', icon: BookOpen, permission: 'menu.view', moduleKey: 'standardization' },
    ],
  },
  {
    id: 'customers',
    label: 'Customers',
    icon: Users,
    to: '/customers',
    matchPrefixes: ['/customers'],
    items: [
      { label: 'Customers', to: '/customers', icon: Users, permission: 'customers.view', excludeRoles: ['cashier', 'employee'], moduleKey: 'customers' },
    ],
  },
  {
    id: 'reporting',
    label: 'Reporting',
    icon: BarChart3,
    to: '/reports',
    matchPrefixes: ['/reports', '/analytics', '/payment-reports', '/register-closures', '/audit-logs'],
    items: [
      { label: 'Reports', to: '/reports', icon: BarChart3, permission: 'reports.view_sales', moduleKey: 'reports' },
      { label: 'Audit Logs', to: '/audit-logs', icon: History, permission: 'audit.view', moduleKey: 'auditLogs' },
    ],
  },
  {
    id: 'setup',
    label: 'Setup',
    icon: Settings,
    to: '/settings',
    matchPrefixes: ['/settings', '/team', '/utilities', '/command-center', '/approvals'],
    items: [
      { label: 'Command Center', to: '/command-center', icon: Command, permission: 'backoffice.access', moduleKey: 'commandCenter' },
      { label: 'Settings', to: '/settings', icon: Settings, permission: 'settings.view' },
      { label: 'Team', to: '/team', icon: Users, permission: 'team.view', moduleKey: 'team' },
      { label: 'Utilities', to: '/utilities', icon: Wrench, superadminOnly: true, moduleKey: 'utilities' },
    ],
  },
];

function matchesPrefix(pathname: string, prefix: string): boolean {
  return pathname === prefix || pathname.startsWith(prefix + '/');
}

/**
 * Resolve which rail group owns the given pathname. Longest matching prefix
 * wins so e.g. /sell/sales-history stays under the Sell group. Falls back to
 * the first group (Home).
 */
export function resolveActiveGroup(groups: LsNavGroup[], pathname: string): LsNavGroup {
  let best: LsNavGroup | null = null;
  let bestLen = -1;
  for (const group of groups) {
    for (const prefix of group.matchPrefixes) {
      if (matchesPrefix(pathname, prefix) && prefix.length > bestLen) {
        best = group;
        bestLen = prefix.length;
      }
    }
  }
  return best ?? groups[0];
}

/**
 * Returns the rail groups visible to the current user, each pruned to the
 * sub-nav items they may access. Empty groups are dropped. Industry-specific
 * labels are applied to match the Blaze sidebar.
 */
export function useLightspeedNav() {
  const { profile, loading } = useAuth();
  const { hasPermission, hasAnyPermission, hasAllPermissions } = usePermissions();
  const { isModuleEnabled, subscriptionLoading } = useModuleSettings();
  const terms = useIndustryTerms();

  const previousRef = useRef<LsNavGroup[]>([]);

  const isItemVisible = (item: LsNavItem): boolean => {
    if (item.superadminOnly) return profile?.role === 'superadmin';

    if (item.excludeRoles && profile?.role && item.excludeRoles.includes(profile.role)) {
      return false;
    }

    if (item.moduleKey) {
      const keys = Array.isArray(item.moduleKey) ? item.moduleKey : [item.moduleKey];
      if (!keys.some((key) => isModuleEnabled(key))) return false;
    }

    if (!item.permission) return true;

    const perms = Array.isArray(item.permission) ? item.permission : [item.permission];
    if (item.requireAll) return hasAllPermissions(perms);
    return perms.length === 1 ? hasPermission(perms[0]) : hasAnyPermission(perms);
  };

  const groups = useMemo(() => {
    // Avoid flicker while auth/subscription data is still settling.
    if ((loading || subscriptionLoading) && previousRef.current.length > 0) {
      return previousRef.current;
    }

    const labelFor = (item: LsNavItem): string => {
      if (item.to === '/kds') return terms.kitchenDisplay;
      if (item.to === '/kitchen-stations') return terms.kitchenStations;
      if (item.to === '/floor-plan') return terms.floorPlan;
      return item.label;
    };

    const filtered = NAV_GROUPS.map((group) => ({
      ...group,
      items: group.items.filter(isItemVisible).map((item) => ({ ...item, label: labelFor(item) })),
    })).filter((group) => group.items.length > 0);

    if (filtered.length > 0) previousRef.current = filtered;
    return filtered;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile, loading, subscriptionLoading, hasPermission, hasAnyPermission, hasAllPermissions, isModuleEnabled, terms]);

  return groups;
}
